import '../../styles/data-table.css'

/**
 * Componente de tabla para visualizar y gestionar categorías de tickets.
 * * **Características**:
 * - Renderizado condicional para estados vacíos.
 * - Tabla compacta de `data-table.css` (tokens --gm-*, claro/oscuro).
 * - Badges dinámicos basados en el estado de eliminación (`deleted`).
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} [props.categories=[]] - Arreglo de objetos de categoría a mostrar.
 * @param {number|string} props.categories[].id - Identificador único de la categoría.
 * @param {string} props.categories[].name - Nombre descriptivo de la categoría.
 * @param {boolean} props.categories[].deleted - Indica si la categoría está marcada como eliminada o activa.
 * @param {Function} [props.onEdit] - Función callback que se dispara al hacer clic en el botón de editar.
 * Recibe el objeto `category` completo como argumento.
 * * @returns {JSX.Element} Una tabla compacta con íconos Font Awesome.
 */
/** Colores de los estados (hex para poder derivar el fondo translúcido). */
const STATUS = {
  active: { label: 'Activa', icon: 'fa-circle-check', color: 'var(--gm-success)' },
  deleted: { label: 'Eliminada', icon: 'fa-circle-xmark', color: 'var(--gm-danger)' },
}

const TicketCategoryTable = ({ categories = [], onEdit }) => {
  return (
    <div className="tt" style={{ flex: 'none' }}>
      <div className="tt-scroll">
        <table className="tt-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th style={{ width: 120 }}>Estado</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>

          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="3" className="tt-empty">
                  <i className="fa-solid fa-tags d-block fs-5 mb-2 opacity-50" />
                  Aún no hay categorías.
                </td>
              </tr>
            ) : (
              categories.map((category) => {
                const status = category.deleted ? STATUS.deleted : STATUS.active
                return (
                  <tr key={category.id}>
                    <td>
                      <span className="fw-semibold">{category.name}</span>
                    </td>
                    <td>
                      <span
                        className="tt-badge gm-tono"
                        style={{ '--tono': status.color }}
                      >
                        <i className={`fa-solid ${status.icon}`} />
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <div className="tt-actions" style={{ opacity: 1 }}>
                        <button
                          type="button"
                          className="tt-icon-btn"
                          onClick={() => onEdit && onEdit(category)}
                          title="Editar categoría"
                          aria-label="Editar categoría"
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TicketCategoryTable
